import { Component, lazy } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay/legacy';
import { connect } from 'react-redux';

import * as tracking from 'dibs-tracking';
import { getAbTestTrackingData } from 'dibs-buyer-layout/exports/clientABTestHelpers';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';
import serverVars from 'server-vars';
import { CUSTOMER_TYPE_TRADE } from '../constants/userConstants';
import { pageTypeConstants as PAGE_TYPES } from '../constants/pageTypeConstants';
import { MIN_THIN_PAGE_COUNT } from '../constants/sbConstants';
import { getPageviewData } from '../utils/tracking/searchBrowse/getPageviewData';

import { BuySharedHead } from './BuySharedHead/BuySharedHead';
import { SharedUserAndRelayVarLoader } from '../shared/SharedUserAndRelayVarLoader';
import { BuySharedBreadcrumbs } from './BuySharedBreadcrumbs/BuySharedBreadcrumbs';
import { BuySharedTitle } from './BuySharedTitle/BuySharedTitle';
import { BuySharedRelatedPages } from './BuySharedRelatedPages/BuySharedRelatedPages';
import { BuySharedResultContainer } from './BuySharedResultContainer/BuySharedResultContainer';
import { SharedPagination } from '../shared/SharedPagination/SharedPagination';
import { BuySharedDescription } from './BuySharedDescription/BuySharedDescription';
import { BuySharedNoInventoryAvailable } from './BuySharedNoInventoryAvailable/BuySharedNoInventoryAvailable';
import { SharedRecentSales } from '../shared/SharedRecentSales/SharedRecentSales';
import { BuySharedCMSFAQ } from './BuySharedCMSFAQ/BuySharedCMSFAQ';
import { SharedFAQ } from '../shared/SharedFAQ/SharedFAQ';
import { BuySharedMoreWaysToBrowse } from './BuySharedMoreWaysToBrowse/BuySharedMoreWaysToBrowse';
import { SharedBottomModule } from '../shared/SharedBottomModule/SharedBottomModule';
import { LoadingComponent } from '../components/global/LoadingComponent';
import { SharedWordpressArticles } from '../shared/SharedWordpressArticles/SharedWordpressArticles';
import { SharedFollowTopButton } from '../shared/SharedFollowTopButton/SharedFollowTopButton';
import { SharedFollowSearchProvider } from '../shared/SharedFollowSearchProvider/SharedFollowSearchProvider';
import { SharedFollowSearchBanner } from '../shared/SharedFollowSearchBanner/SharedFollowSearchBanner';

import { BuyRespTopNav } from './BuyRespTopNav/BuyRespTopNav';
import { BuyMobileNav } from './BuyMobileNav/BuyMobileNav';
import { ServerVarsContextConsumer } from '../global/ServerVarsContext/ServerVarsContext';
import { BuySharedContentModuleRegular } from './BuySharedContentModuleRegular/BuySharedContentModuleRegular';

import { updateRelayVariables } from '../actions/sharedRelayVariableActions';
import { toggleRequestInFlight, updateUriRef } from '../actions/filterActions';

import { getNewPaginationUriRef, addCdnQueryParams } from '../utils/uriUtils';

import { FindingRouter } from '../router/FindingRouter';

import classNames from 'classnames';

import styles from './BuySharedLayout.scss';

const SharedYMALLazyComponent = lazy(() =>
    import(/* webpackChunkName: "SharedYMAL" */ '../shared/SharedYMAL/SharedYMAL')
);

class BuySharedLayoutComponent extends Component {
    constructor() {
        super();

        this.handleUserRefetch = this.handleUserRefetch.bind(this);
        this.handleBuyPageRefetch = this.handleBuyPageRefetch.bind(this);
        this.refetchCallback = this.refetchCallback.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
    }

    componentDidMount() {
        const { viewer } = this.props;
        const { buyPage } = viewer;
        const { pageType, totalResults, appliedFilters, buyPageSbUriRef: displayUriRef } = buyPage;
        this.buyPageName = appliedFilters.find(
            f => f.name === PAGE_TYPES.BUY
        )?.values?.[0]?.urlLabel;
        this.router = new FindingRouter({ routes: [{ path: '/buy/:pageName?' }] });
        this.props.updateUriRef({ uriRef: displayUriRef });
        tracking.trackPageview({
            additional: {
                ...getPageviewData({
                    pageType,
                    totalResults,
                    appliedFilters,
                }),
            },
        });
        const trackingData = getAbTestTrackingData(document.cookie, 'NEW_TAB_TEST');
        tracking.trackAbTestEvent(trackingData, 'results_pageview');
    }

    componentDidUpdate(prevProps) {
        const { generatedUriRef, page: newPage } = this.props;
        const oldUriRef = addCdnQueryParams(prevProps.generatedUriRef);
        const newUriRef = addCdnQueryParams(generatedUriRef);
        const uriRefChanged = oldUriRef !== '' && newUriRef !== oldUriRef;
        const pageChanged = newPage !== prevProps.page;
        let uriRef = generatedUriRef;

        if (pageChanged) {
            uriRef = getNewPaginationUriRef(generatedUriRef, newPage);
            this.handlePageChange(newPage);
        }

        if (uriRefChanged) {
            const variables = { uriRef, page: newPage };
            this.props.toggleRequestInFlight(true);
            this.handleBuyPageRefetch(
                { ...variables, fetchBuy: true, skipUser: true },
                null,
                this.refetchCallback
            );
            this.props.updateRelayVariables(variables);
        }
    }

    refetchCallback() {
        this.props.toggleRequestInFlight(false);
        this.props.updateUriRef({ uriRef: this.props?.viewer?.buyPage?.buyPageSbUriRef });
    }

    handlePageChange(page) {
        if (this.buyPageName) {
            this.router.navigate(
                getNewPaginationUriRef(`/${PAGE_TYPES.BUY}/${this.buyPageName}/`, page),
                { replace: true }
            );
        }
    }

    handleUserRefetch(variables, renderVariables, callback) {
        /**
         * Need to refetch sb items for trade users, the price depends on the `isTrade` variable
         * being set.
         */
        const { viewer, relayVariables } = this.props;
        const { displayUriRef } = viewer?.buyPage || {};
        const { followSearchPages, priceBookName } = relayVariables || {};
        const fetchBuy = variables.isTrade && serverVars.get('userType') !== CUSTOMER_TYPE_TRADE;
        this.handleBuyPageRefetch(
            {
                ...variables,
                fetchBuy,
                skipUser: false,
                followSearchPages: displayUriRef ? [displayUriRef] : followSearchPages,
                priceBookName,
                /**
                 * There's a chance that the server side `userCountryCode` and the client side one will not match.
                 *
                 * One case that we know of is when you're on your devbox, since env-boot doesn't do any country detection.
                 *
                 * In that case server side and client side relay variables will be different, we won't be able to reuse the
                 * server side data for `itemSearch`, it will be `null` and the page will crash.
                 *
                 * To prevent that, for the initial refetch query, we will always use the server side value for `userCountryCode`.
                 */
                userCountryCode: fetchBuy
                    ? variables?.userCountryCode
                    : relayVariables?.userCountryCode,
            },
            renderVariables,
            callback
        );
    }

    handleBuyPageRefetch(variables, renderVariables, callback) {
        this.props.relay.refetch(variables, renderVariables, callback);
    }

    render() {
        const { viewer, relay, isClient, page, refetchInFlight } = this.props;
        const {
            buyPage,
            user = null,
            isWordpressArticlesEnabled,
            isNlgContentEnabled,
            isRelatedBuyPagesEnabled,
            isAllBuyPagesRedesignEnabled,
        } = viewer;
        const {
            internalBuyLinks,
            browseContentModule,
            bottomModule,
            filters,
            buyPageSbUriRef,
            displayMaxNumberOfPages,
            totalResults,
            appliedFilters,
            displayUriRef,
            meta: { buyPageAvailableItemCount },
        } = buyPage;

        const isThinPage = totalResults < MIN_THIN_PAGE_COUNT;
        const searchTerm = appliedFilters.find(f => f.name === PAGE_TYPES.BUY)?.values?.[0]
            ?.displayName;

        // Available item count for redesign is increased from 4 to 30 (GROWTH-7399)
        const showBuyPageRedesign =
            isAllBuyPagesRedesignEnabled || buyPageAvailableItemCount <= MIN_THIN_PAGE_COUNT;

        return (
            <ServerVarsContextConsumer>
                {({ isMobile }) => (
                    <SharedFollowSearchProvider
                        user={user}
                        itemSearch={buyPage}
                        uriRef={displayUriRef}
                        searchTerm={searchTerm}
                        thinTrackingEnabled={isThinPage}
                    >
                        {({
                            showFollowButton,
                            isFollowing,
                            followSearchValue,
                            refetchFollow,
                            isCategorySearch,
                        }) => (
                            <>
                                <BuySharedHead buyPage={buyPage} />
                                <SharedUserAndRelayVarLoader
                                    onUserRefetch={this.handleUserRefetch}
                                    environment={relay.environment}
                                />
                                {isMobile && refetchInFlight && <LoadingComponent />}
                                <BuySharedBreadcrumbs buyPage={buyPage} />
                                <div className={styles.headerLeftWrapper}>
                                    <div className={styles.headerLeft}>
                                        <BuySharedTitle buyPage={buyPage} />
                                        {!isMobile && (
                                            <SharedFollowTopButton
                                                viewer={viewer}
                                                user={user}
                                                showFollowButton={showFollowButton}
                                                followSearchValue={followSearchValue}
                                                isFollowing={isFollowing}
                                                refetchFollow={refetchFollow}
                                                isCategorySearch={isCategorySearch}
                                                itemSearch={buyPage}
                                                className={styles.followTopButton}
                                            />
                                        )}
                                    </div>
                                </div>

                                {isRelatedBuyPagesEnabled && (
                                    <BuySharedRelatedPages buyPage={buyPage} />
                                )}

                                <BuySharedContentModuleRegular
                                    browseContentModule={browseContentModule}
                                />

                                {buyPageAvailableItemCount > MIN_THIN_PAGE_COUNT ? (
                                    <>
                                        {isMobile ? (
                                            <BuyMobileNav
                                                viewer={viewer}
                                                buyPage={buyPage}
                                                user={user}
                                                filters={filters}
                                                isClient={isClient}
                                            />
                                        ) : (
                                            <BuyRespTopNav
                                                viewer={viewer}
                                                buyPage={buyPage}
                                                user={user}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <div className={styles.spacer} />
                                )}

                                <div className={styles.container}>
                                    {totalResults ? (
                                        <>
                                            <BuySharedResultContainer
                                                viewer={viewer}
                                                buyPage={buyPage}
                                                user={user}
                                                relay={relay}
                                            />
                                            <SharedPagination
                                                currentPage={page}
                                                numberOfPages={displayMaxNumberOfPages}
                                                displayUriRef={buyPageSbUriRef}
                                                hideHref
                                            />
                                        </>
                                    ) : (
                                        <BuySharedNoInventoryAvailable />
                                    )}

                                    {/* Follow Search */}
                                    <SharedFollowSearchBanner
                                        viewer={viewer}
                                        user={user}
                                        showFollowButton={showFollowButton}
                                        isFollowing={isFollowing}
                                        followSearchValue={followSearchValue}
                                        uriRef={displayUriRef}
                                        buttonSize="large"
                                        onFollowSearchCompleted={refetchFollow}
                                        isCategorySearch={isCategorySearch}
                                        itemSearch={buyPage}
                                    />

                                    {showBuyPageRedesign && (
                                        <>
                                            <SharedRecentSales
                                                user={user}
                                                viewer={viewer}
                                                itemSearch={buyPage}
                                                isMobile={isMobile}
                                                isClient={isClient}
                                                relay={relay}
                                            />
                                            <ClientSuspense fallback="">
                                                <SharedYMALLazyComponent
                                                    user={user}
                                                    viewer={viewer}
                                                    itemSearch={buyPage}
                                                    isMobile={isMobile}
                                                    isClient={isClient}
                                                    relay={relay}
                                                />
                                            </ClientSuspense>
                                        </>
                                    )}
                                    {isNlgContentEnabled && (
                                        <SharedBottomModule
                                            className={classNames(styles.bottomModule, {
                                                [styles.topMargin]: showBuyPageRedesign,
                                            })}
                                            bottomModule={bottomModule}
                                        />
                                    )}
                                    <BuySharedDescription buyPage={buyPage} />
                                </div>

                                <BuySharedCMSFAQ buyPage={buyPage} />

                                {/* Questions & Answers */}
                                <SharedFAQ itemSearch={buyPage} />

                                {isWordpressArticlesEnabled && (
                                    <SharedWordpressArticles
                                        itemSearch={buyPage}
                                        headerClassName={styles.wpArticlesHeader}
                                    />
                                )}

                                <BuySharedMoreWaysToBrowse
                                    viewer={viewer}
                                    itemSearch={buyPage}
                                    internalBuyLinks={internalBuyLinks}
                                    popularSearchTermClassName={styles.popularSearchCategoryTerm}
                                />
                            </>
                        )}
                    </SharedFollowSearchProvider>
                )}
            </ServerVarsContextConsumer>
        );
    }
}

function mapStateToProps(state) {
    const { filters, relayVariables } = state;
    const { generatedUriRef, page } = filters;
    const {
        variables: { isClient },
    } = relayVariables;

    return {
        page,
        generatedUriRef,
        refetchInFlight: filters.refetchInFlight,
        isClient,
        relayVariables: relayVariables.variables,
    };
}

BuySharedLayoutComponent.propTypes = {
    viewer: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    generatedUriRef: PropTypes.string,
    isClient: PropTypes.bool.isRequired,
    toggleRequestInFlight: PropTypes.func.isRequired,
    updateUriRef: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    refetchInFlight: PropTypes.bool.isRequired,
    updateRelayVariables: PropTypes.func.isRequired,
    buyPage: PropTypes.object,
    relayVariables: PropTypes.shape({
        followSearchPages: PropTypes.arrayOf(PropTypes.string),
        userCountryCode: PropTypes.string,
        priceBookName: PropTypes.string,
    }).isRequired,
};

export const BuySharedLayout = createRefetchContainer(
    connect(mapStateToProps, { toggleRequestInFlight, updateUriRef, updateRelayVariables })(
        BuySharedLayoutComponent
    ),
    {
        viewer: graphql`
            fragment BuySharedLayout_viewer on Viewer {
                isWordpressArticlesEnabled: isEnabledByLocale(featureName: "WP_EDITORIAL")
                isNlgContentEnabled: isEnabledByLocale(featureName: "NLG_CONTENT")
                isRelatedBuyPagesEnabled: isEnabledByLocale(featureName: "RELATED_BUY_PAGES")
                isAllBuyPagesRedesignEnabled: featureFlag(feature: "ALL_BUY_PAGE_REDESIGN")
                buyPage: itemSearch(
                    uriRef: $uriRef
                    localeOrigin: $localeOrigin
                    first: $first
                    page: $page
                    buyPageId: $rootId
                    userCountryCode: $userCountryCode
                    priceBookName: $priceBookName
                ) {
                    buyPageSbUriRef
                    pageType
                    totalResults
                    displayMaxNumberOfPages
                    displayUriRef
                    appliedFilters {
                        name
                        values {
                            displayName
                            urlLabel
                            code
                        }
                    }
                    meta {
                        buyPageAvailableItemCount
                    }
                    filters {
                        ...BuyMobileNav_filters
                    }
                    browseContentModule {
                        ...BuySharedContentModuleRegular_browseContentModule
                    }
                    bottomModule {
                        ...SharedBottomModule_bottomModule
                    }
                    ...BuySharedBreadcrumbs_buyPage
                    ...BuySharedTitle_buyPage
                    ...BuySharedRelatedPages_buyPage
                    ...BuySharedResultContainer_buyPage
                    ...SharedRecentSales_itemSearch
                    ...SharedYMAL_itemSearch
                        @arguments(userCountryCode: $userCountryCode, priceBookName: $priceBookName)
                    ...BuySharedDescription_buyPage
                    ...BuySharedHead_buyPage
                    ...BuySharedCMSFAQ_buyPage
                    ...SharedFAQ_itemSearch
                    ...SharedWordpressArticles_itemSearch
                    ...BuySharedMoreWaysToBrowse_itemSearch
                    internalBuyLinks {
                        ...BuySharedMoreWaysToBrowse_internalBuyLinks
                    }
                    ...BuyRespTopNav_buyPage
                    ...BuyMobileNav_buyPage
                    ...SharedFollowSearchProvider_itemSearch
                    ...SharedFollowTopButton_itemSearch
                    ...SharedFollowSearchBanner_itemSearch
                }
                ...SharedRecentSales_viewer
                ...BuySharedMoreWaysToBrowse_viewer
                ...BuySharedResultContainer_viewer
                ...BuyRespTopNav_viewer
                ...BuyMobileNav_viewer
                user(userId: $userId) @include(if: $fetchUser) {
                    ...BuyRespTopNav_user
                    ...BuyMobileNav_user
                    ...BuySharedResultContainer_user
                    ...SharedRecentSales_user
                    ...SharedYMAL_user
                    ...SharedFollowSearchProvider_user
                    ...SharedFollowTopButton_user
                    ...SharedFollowSearchBanner_user
                }
                ...SharedFollowSearchBanner_viewer
                ...SharedFollowTopButton_viewer
            }
        `,
    },
    graphql`
        query BuySharedLayoutRefetchQuery(
            $uriRef: String!
            $localeOrigin: String!
            $isTrade: Boolean!
            $showSeller: Boolean!
            $first: Int!
            $page: Int!
            $rootId: String!
            $fetchBuy: Boolean!
            $userZipCode: String = ""
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
            $userId: String = ""
            $fetchUser: Boolean!
            $skipUser: Boolean = true
            $followSearchTypes: [types] = [SEARCH, USER_PREFERENCE]
            $followSearchPages: [String]
            $priceBookName: String
        ) {
            viewer {
                isWordpressArticlesEnabled: isEnabledByLocale(featureName: "WP_EDITORIAL")
                buyPage: itemSearch(
                    uriRef: $uriRef
                    localeOrigin: $localeOrigin
                    first: $first
                    page: $page
                    buyPageId: $rootId
                    userCountryCode: $userCountryCode
                    priceBookName: $priceBookName
                ) @include(if: $fetchBuy) {
                    pageType
                    totalResults
                    displayMaxNumberOfPages
                    buyPageSbUriRef
                    appliedFilters {
                        name
                        values {
                            displayName
                            urlLabel
                            code
                        }
                    }
                    meta {
                        buyPageAvailableItemCount
                    }
                    browseContentModule {
                        ...BuySharedContentModuleRegular_browseContentModule
                    }
                    bottomModule {
                        ...SharedBottomModule_bottomModule
                    }
                    ...BuySharedBreadcrumbs_buyPage
                    ...BuySharedTitle_buyPage
                    ...BuySharedRelatedPages_buyPage
                    ...BuySharedResultContainer_buyPage
                    ...SharedRecentSales_itemSearch
                    ...SharedYMAL_itemSearch
                        @arguments(userCountryCode: $userCountryCode, priceBookName: $priceBookName)
                    ...BuySharedDescription_buyPage
                    ...BuySharedHead_buyPage
                    ...BuySharedCMSFAQ_buyPage
                    ...SharedFAQ_itemSearch
                    ...SharedWordpressArticles_itemSearch
                    ...BuySharedMoreWaysToBrowse_itemSearch
                    internalBuyLinks {
                        ...BuySharedMoreWaysToBrowse_internalBuyLinks
                    }
                    ...BuyRespTopNav_buyPage
                    ...SharedFollowSearchProvider_itemSearch
                    ...SharedFollowTopButton_itemSearch
                }

                user(userId: $userId) @include(if: $fetchUser) @skip(if: $skipUser) {
                    ...BuyRespTopNav_user
                    ...BuyMobileNav_user
                    ...BuySharedResultContainer_user
                    ...SharedRecentSales_user
                    ...SharedYMAL_user
                    ...SharedFollowSearchProvider_user
                    ...SharedFollowTopButton_user
                    ...SharedFollowSearchBanner_user
                }
                ...BuySharedResultContainer_viewer
                ...SharedRecentSales_viewer
            }
        }
    `
);
